import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, Form, Formik } from "formik";
import { Col, Row } from "react-bootstrap";
import { bool, func, number, shape } from "prop-types";
import { actions as comexActions } from "reducers/comex";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as config from "util/config";
import useSchema from "hooks/useSchema";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
// eslint-disable-next-line import/no-named-as-default
import AmountField from "pages/_components/fields/formik/AmountField";
import Selector from "pages/_components/fields/formik/Selector";
import Date from "pages/_components/fields/DateField";
import CustomText from "pages/_components/CustomText/CustomText";

const FORM_ID = "comex.clearance";

const ClearanceForm = ({
    isDesktop,
    handleAddItem,
    handleDeleteItem,
    index,
    clearenceData,
    numberOfClearances,
    updateItemsCompleted,
    handleUpdateItem,
    numberOfEditableItems,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const { stringRequired } = useSchema();
    const { comexCurrencies = [] } = useSelector((state) => state.comex);

    const [collapsed, setCollapsed] = useState(false);
    const [maxNumberOfClearances, setMaxNumberOfClearances] = useState(5);
    const [minLengthNumber, setMinLengthNumber] = useState(1);
    const [maxLengthNumber, setMaxLengthNumber] = useState(16);
    const [minLengthFob, setMinLengthFob] = useState(1);
    const [maxLengthFob, setMaxLengthFob] = useState(20);
    const [minLengthFreight, setMinLengthFreight] = useState(1);
    const [maxLengthFreight, setMaxLength] = useState(20);
    const [minLengthInsurance, setMinLengthInsurance] = useState(1);
    const [maxLengthInsurance, setMaxLengthInsurance] = useState(20);
    const [minLengthAmount, setMinLengthAmount] = useState(1);
    const [maxLengthAmount, setMaxLengthAmount] = useState(20);
    const [minLengthEntity, setMinLengthEntity] = useState(1);
    const [maxLengthEntity, setMaxLengthEntity] = useState(50);
    const [minLengthInvoiceNumber, setMinLengthInvoiceNumber] = useState(1);
    const [maxLengthInvoiceNumber, setMaxLengthInvoiceNumber] = useState(20);
    const [minLengthOutstandingDebt, setMinLengthOutstandingDebt] = useState(1);
    const [maxLengthOutstandingDebt, setMaxLengthOutstandingDebt] = useState(20);
    const [alphanumericValidation, setAlphanumericValidation] = useState("");

    useEffect(() => {
        dispatch(comexActions.getComexSelectors());

        const maxItems = config.get("comex.clearance.max.items", 5);
        const minNumber = config.get("comex.clearance.number.length.min", 1);
        const maxNumber = config.get("comex.clearance.number.length.max", 16);
        const minFob = config.get("comex.clearance.fob.length.min", 1);
        const maxFob = config.get("comex.clearance.fob.length.max", 20);
        const minFreight = config.get("comex.clearance.freight.length.min", 1);
        const maxFreight = config.get("comex.clearance.freight.length.max", 20);
        const minInsurance = config.get("comex.clearance.insurance.length.min", 1);
        const maxInsurance = config.get("comex.clearance.insurance.length.max", 20);
        const minAmount = config.get("comex.clearance.amount.length.min", 1);
        const maxAmount = config.get("comex.clearance.amount.length.max", 20);
        const minEntity = config.get("comex.clearance.entity.length.min", 1);
        const maxEntity = config.get("comex.clearance.entity.length.max", 50);
        const minInvoiceNumber = config.get("comex.clearance.invoiceNumber.length.min", 1);
        const maxInvoiceNumber = config.get("comex.clearance.invoiceNumber.length.max", 20);
        const minOutstandingDebt = config.get("comex.clearance.outstandingDebt.length.min", 1);
        const maxOutstandingDebt = config.get("comex.clearance.outstandingDebt.length.max", 20);
        const validation = config.get("alphanumeric.validationFormat", /^[a-zA-Z0-9]*$/);

        setMaxNumberOfClearances(maxItems);
        setMinLengthNumber(minNumber);
        setMaxLengthNumber(maxNumber);
        setMinLengthFob(minFob);
        setMaxLengthFob(maxFob);
        setMinLengthFreight(minFreight);
        setMaxLength(maxFreight);
        setMinLengthInsurance(minInsurance);
        setMaxLengthInsurance(maxInsurance);
        setMinLengthAmount(minAmount);
        setMaxLengthAmount(maxAmount);
        setMinLengthEntity(minEntity);
        setMaxLengthEntity(maxEntity);
        setMinLengthInvoiceNumber(minInvoiceNumber);
        setMaxLengthInvoiceNumber(maxInvoiceNumber);
        setMinLengthOutstandingDebt(minOutstandingDebt);
        setMaxLengthOutstandingDebt(maxOutstandingDebt);
        setAlphanumericValidation(validation);
    }, []);

    useEffect(() => {
        const x = document.getElementById(`collapsibleForm-${index}`);
        x.style.display = "grid";
    }, []);

    const updateCollapsed = () => {
        setCollapsed(!collapsed);

        const x = document.getElementById(`collapsibleForm-${index}`);
        if (x.style.display === "none") {
            x.style.display = "grid";
        } else {
            x.style.display = "none";
        }
    };

    const allFieldsCompleted = (values) => {
        const {
            clearanceNumber,
            fobCurrency,
            fob,
            freightCurrency,
            freight,
            insuranceCurrency,
            insurance,
            amountCurrency,
            amount,
            followUpEntity,
            invoiceNumber,
            officializationDate,
            shipmentDate,
            outstandingDebt,
            outstandingDebtCurrency,
        } = values;

        return (
            !!clearanceNumber &&
            !!fobCurrency &&
            !!fob &&
            !!freightCurrency &&
            !!freight &&
            !!insuranceCurrency &&
            !!insurance &&
            !!amountCurrency &&
            !!amount &&
            !!followUpEntity &&
            !!invoiceNumber &&
            !!officializationDate &&
            !!shipmentDate &&
            !!outstandingDebt &&
            !!outstandingDebtCurrency
        );
    };

    const submitIsAllowed = (values) => numberOfClearances < maxNumberOfClearances && allFieldsCompleted(values);

    useEffect(() => {
        if (clearenceData && Object.keys(clearenceData).length !== 0) {
            formRef.current.setFieldValue("clearanceNumber", clearenceData?.clearanceNumber, false);
            formRef.current.setFieldValue("fobCurrency", clearenceData?.fobCurrency, false);
            formRef.current.setFieldValue("fob", clearenceData?.fob, false);
            formRef.current.setFieldValue("freightCurrency", clearenceData?.freightCurrency, false);
            formRef.current.setFieldValue("freight", clearenceData?.freight, false);
            formRef.current.setFieldValue("insuranceCurrency", clearenceData?.insuranceCurrency, false);
            formRef.current.setFieldValue("insurance", clearenceData?.insurance, false);
            formRef.current.setFieldValue("amountCurrency", clearenceData?.amountCurrency, false);
            formRef.current.setFieldValue("amount", clearenceData?.amount, false);
            formRef.current.setFieldValue("followUpEntity", clearenceData?.followUpEntity, false);
            formRef.current.setFieldValue("invoiceNumber", clearenceData?.invoiceNumber, false);
            formRef.current.setFieldValue("officializationDate", clearenceData?.officializationDate, false);
            formRef.current.setFieldValue("shipmentDate", clearenceData?.shipmentDate, false);
            formRef.current.setFieldValue("outstandingDebt", clearenceData?.outstandingDebt, false);
            formRef.current.setFieldValue("outstandingDebtCurrency", clearenceData?.outstandingDebtCurrency, false);
            if (allFieldsCompleted(clearenceData)) {
                updateItemsCompleted(true);
            } else {
                updateItemsCompleted(false);
            }
        } else {
            formRef.current.resetForm({
                clearanceNumber: "",
                fobCurrency: "",
                fob: "",
                freightCurrency: "",
                freight: "",
                insuranceCurrency: "",
                insurance: "",
                amountCurrency: "",
                amount: "",
                followUpEntity: "",
                invoiceNumber: "",
                officializationDate: "",
                shipmentDate: "",
                outstandingDebt: "",
                outstandingDebtCurrency: "",
            });
        }
    }, [clearenceData]);

    const validationSchema = Yup.object().shape({
        clearanceNumber: stringRequired
            .min(minLengthNumber, i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthNumber))
            .max(maxLengthNumber, i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthNumber)),
        fobCurrency: stringRequired,
        fob: Yup.object().shape({
            amount: stringRequired
                .min(minLengthFob, i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthFob))
                .max(maxLengthFob, i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthFob)),
        }),
        freightCurrency: stringRequired,
        freight: Yup.object().shape({
            amount: stringRequired
                .min(minLengthFreight, i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthFreight))
                .max(maxLengthFreight, i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthFreight)),
        }),
        insuranceCurrency: stringRequired,
        insurance: Yup.object().shape({
            amount: stringRequired
                .min(minLengthInsurance, i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthInsurance))
                .max(maxLengthInsurance, i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthInsurance)),
        }),
        amountCurrency: stringRequired,
        amount: Yup.object().shape({
            amount: stringRequired
                .min(minLengthAmount, i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthAmount))
                .max(maxLengthAmount, i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthAmount)),
        }),
        followUpEntity: stringRequired
            .min(minLengthEntity, i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthEntity))
            .max(maxLengthEntity, i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthEntity)),
        invoiceNumber: stringRequired
            .min(
                minLengthInvoiceNumber,
                i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthInvoiceNumber),
            )
            .max(
                maxLengthInvoiceNumber,
                i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthInvoiceNumber),
            ),
        officializationDate: stringRequired.nullable(),
        shipmentDate: stringRequired.nullable(),
        outstandingDebt: Yup.object().shape({
            amount: stringRequired
                .min(
                    minLengthOutstandingDebt,
                    i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthOutstandingDebt),
                )
                .max(
                    maxLengthOutstandingDebt,
                    i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthOutstandingDebt),
                ),
        }),
        outstandingDebtCurrency: stringRequired,
    });

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        validationSchema,
        onSubmit: () => {
            handleAddItem();
        },
    };

    const deleteClearance = () => {
        handleDeleteItem();
    };

    const showCollapseButton = (idx, formCompleted) => formCompleted || numberOfEditableItems > 1;

    const handleUpdateForm = (formData) => {
        handleUpdateItem(formData, index, allFieldsCompleted(formData));
    };

    const handleOnChangeField = (value, name, formData) => {
        const data = { ...formData };
        data[name] = value;
        handleUpdateForm(data);
    };

    const handleOnChangeAmount = (value, name, formData) => {
        const data = { ...formData };

        if (value) {
            formRef.current.setFieldValue(`${name}.amount`, value);
            data[name] = { amount: value };
        } else {
            formRef.current.setFieldValue(`${name}.amount`, "");
            data[name] = "";
        }

        handleUpdateForm(data);
    };

    return (
        <Formik {...formikProps} ref={formRef}>
            {(allFormik) => (
                <Form>
                    <Row className="clearance-form-row">
                        <Col xs={8} md={1}>
                            <div className="comex-clearance-table-item">
                                <Field
                                    component={TextField}
                                    idForm={FORM_ID}
                                    name="clearanceNumber"
                                    labelText={FORM_ID.concat(".number.label")}
                                    hidePlaceholder
                                    pattern={alphanumericValidation}
                                    disableUseFieldOnBlur
                                    handleOnChange={(ev) =>
                                        handleOnChangeField(ev.target.value, "clearanceNumber", allFormik.values)
                                    }
                                    minLength={minLengthNumber}
                                    maxLength={maxLengthNumber}
                                />
                                {showCollapseButton(index, allFieldsCompleted(allFormik.values)) && (
                                    <Button
                                        className="comex-clearance-collapse-btn"
                                        hideLabel
                                        onClick={() => updateCollapsed()}
                                        image={collapsed ? "selectArrowUp.svg" : "styledmenu-arrow-down.svg"}
                                    />
                                )}
                            </div>
                        </Col>
                        {numberOfEditableItems > 1 && isDesktop && (
                            <Col xs={8} md={1} style={{ marginTop: "32px" }}>
                                <Button
                                    className="btn-outline comex-clearance-table-delete-btn"
                                    image="images/figma/icon/cancel_green.svg"
                                    label="comex.clearance.delete.btn"
                                    onClick={() => deleteClearance()}
                                />
                            </Col>
                        )}
                    </Row>
                    <div id={`collapsibleForm-${index}`}>
                        <Row className="clearance-form-row">
                            <Col xs={8} md={1}>
                                <div className="item-with-currency">
                                    <div className="item-with-currency-row">
                                        <h4>
                                            <CustomText text={FORM_ID.concat(".fob.label")} textType="I18N" />
                                        </h4>
                                    </div>
                                    <div className="item-with-currency-row">
                                        <div className="online-item-currency">
                                            <Field
                                                component={Selector}
                                                name="fobCurrency"
                                                options={comexCurrencies.map(({ id }) => ({
                                                    value: id,
                                                    label: id,
                                                }))}
                                                placeholder="Divisa"
                                                onChange={(value) =>
                                                    handleOnChangeField(value, "fobCurrency", allFormik.values)
                                                }
                                                hideLabel
                                                disableUseFieldOnBlur
                                            />
                                        </div>
                                        <div>
                                            <Field
                                                component={AmountField}
                                                idForm={FORM_ID}
                                                hideLabel
                                                name="fob"
                                                hidePlaceholder
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                onInputChange={(ev) =>
                                                    handleOnChangeAmount(ev.target.value, "fob", allFormik.values)
                                                }
                                                decimalLength={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="item-with-currency">
                                    <div className="item-with-currency-row">
                                        <h4>
                                            <CustomText text={FORM_ID.concat(".freight.label")} textType="I18N" />
                                        </h4>
                                    </div>
                                    <div className="item-with-currency-row">
                                        <div className="online-item-currency">
                                            <Field
                                                component={Selector}
                                                name="freightCurrency"
                                                options={comexCurrencies.map(({ id }) => ({
                                                    value: id,
                                                    label: id,
                                                }))}
                                                placeholder="Divisa"
                                                onChange={(value) =>
                                                    handleOnChangeField(value, "freightCurrency", allFormik.values)
                                                }
                                                hideLabel
                                                disableUseFieldOnBlur
                                            />
                                        </div>
                                        <div>
                                            <Field
                                                component={AmountField}
                                                idForm={FORM_ID}
                                                hideLabel
                                                name="freight"
                                                hidePlaceholder
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                onInputChange={(ev) =>
                                                    handleOnChangeAmount(ev.target.value, "freight", allFormik.values)
                                                }
                                                decimalLength={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="item-with-currency">
                                    <div className="item-with-currency-row">
                                        <h4>
                                            <CustomText text={FORM_ID.concat(".insurance.label")} textType="I18N" />
                                        </h4>
                                    </div>
                                    <div className="item-with-currency-row">
                                        <div className="online-item-currency">
                                            <Field
                                                component={Selector}
                                                name="insuranceCurrency"
                                                options={comexCurrencies.map(({ id }) => ({
                                                    value: id,
                                                    label: id,
                                                }))}
                                                placeholder="Divisa"
                                                onChange={(value) =>
                                                    handleOnChangeField(value, "insuranceCurrency", allFormik.values)
                                                }
                                                hideLabel
                                                disableUseFieldOnBlur
                                            />
                                        </div>
                                        <div>
                                            <Field
                                                component={AmountField}
                                                idForm={FORM_ID}
                                                hideLabel
                                                name="insurance"
                                                hidePlaceholder
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                onInputChange={(ev) =>
                                                    handleOnChangeAmount(ev.target.value, "insurance", allFormik.values)
                                                }
                                                decimalLength={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="item-with-currency">
                                    <div className="item-with-currency-row">
                                        <h4>
                                            <CustomText text={FORM_ID.concat(".amount.label")} textType="I18N" />
                                        </h4>
                                    </div>
                                    <div className="item-with-currency-row">
                                        <div className="online-item-currency">
                                            <Field
                                                component={Selector}
                                                name="amountCurrency"
                                                options={comexCurrencies.map(({ id }) => ({
                                                    value: id,
                                                    label: id,
                                                }))}
                                                placeholder="Divisa"
                                                hideLabel
                                                disableUseFieldOnBlur
                                            />
                                        </div>
                                        <div>
                                            <Field
                                                component={AmountField}
                                                idForm={FORM_ID}
                                                hideLabel
                                                name="amount"
                                                hidePlaceholder
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                onInputChange={(ev) =>
                                                    handleOnChangeAmount(ev.target.value, "amount", allFormik.values)
                                                }
                                                decimalLength={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="comex-clearance-table-item" style={{ marginTop: "10px" }}>
                                    <Field
                                        component={TextField}
                                        idForm={FORM_ID}
                                        name="followUpEntity"
                                        hidePlaceholder
                                        minLength={minLengthEntity}
                                        maxLength={maxLengthEntity}
                                        pattern={alphanumericValidation}
                                        handleOnChange={(ev) =>
                                            handleOnChangeField(ev.target.value, "followUpEntity", allFormik.values)
                                        }
                                        disableUseFieldOnBlur
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row className="clearance-form-row">
                            <Col xs={8} md={1}>
                                <div className="comex-clearance-table-item">
                                    <Field
                                        component={TextField}
                                        idForm={FORM_ID}
                                        name="invoiceNumber"
                                        hidePlaceholder
                                        minLength={minLengthInvoiceNumber}
                                        maxLength={maxLengthInvoiceNumber}
                                        pattern={alphanumericValidation}
                                        handleOnChange={(ev) =>
                                            handleOnChangeField(ev.target.value, "invoiceNumber", allFormik.values)
                                        }
                                        disableUseFieldOnBlur
                                    />
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="comex-clearance-table-item text-left">
                                    <Field
                                        component={Date}
                                        idForm={FORM_ID}
                                        name="officializationDate"
                                        hidePlaceholder
                                        handleChange={(ev) => {
                                            if (ev) {
                                                handleOnChangeField(ev._d, "officializationDate", allFormik.values);
                                            } else {
                                                handleOnChangeField("", "officializationDate", allFormik.values);
                                            }
                                        }}
                                    />
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="comex-clearance-table-item text-left">
                                    <Field
                                        component={Date}
                                        idForm={FORM_ID}
                                        name="shipmentDate"
                                        handleChange={(ev) => {
                                            if (ev) {
                                                handleOnChangeField(ev._d, "shipmentDate", allFormik.values);
                                            } else {
                                                handleOnChangeField("", "shipmentDate", allFormik.values);
                                            }
                                        }}
                                        hidePlaceholder
                                    />
                                </div>
                            </Col>
                            <Col xs={8} md={1}>
                                <div className="item-with-currency">
                                    <div className="item-with-currency-row label">
                                        <h4>
                                            <CustomText
                                                text={FORM_ID.concat(".outstandingDebt.label")}
                                                textType="I18N"
                                            />
                                        </h4>
                                    </div>
                                    <div className="item-with-currency-row">
                                        <div className="online-item-currency">
                                            <Field
                                                component={Selector}
                                                name="outstandingDebtCurrency"
                                                options={comexCurrencies.map(({ id }) => ({
                                                    value: id,
                                                    label: id,
                                                }))}
                                                placeholder="Divisa"
                                                onChange={(value) =>
                                                    handleOnChangeField(
                                                        value,
                                                        "outstandingDebtCurrency",
                                                        allFormik.values,
                                                    )
                                                }
                                                hideLabel
                                                disableUseFieldOnBlur
                                            />
                                        </div>
                                        <div>
                                            <Field
                                                component={AmountField}
                                                idForm={FORM_ID}
                                                hideLabel
                                                name="outstandingDebt"
                                                hidePlaceholder
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                decimalLength={2}
                                                onInputChange={(ev) =>
                                                    handleOnChangeAmount(
                                                        ev.target.value,
                                                        "outstandingDebt",
                                                        allFormik.values,
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            {isDesktop && (
                                <Col xs={8} md={1}>
                                    <Button
                                        type="submit"
                                        className="btn-outline comex-clearance-table-new-btn"
                                        image="images/figma/icon/nuevo.svg"
                                        label="comex.clearance.new.btn"
                                        disabled={
                                            !submitIsAllowed(allFormik.values) ||
                                            (numberOfEditableItems > 1 && index < numberOfEditableItems - 1)
                                        }
                                    />
                                </Col>
                            )}
                        </Row>
                        {!isDesktop && (
                            <div className="clearance-mobile-buttons">
                                {numberOfEditableItems > 1 && (
                                    <Button
                                        className="btn-outline comex-clearance-table-delete-btn"
                                        image="images/figma/icon/cancel_green.svg"
                                        label="comex.clearance.delete.btn"
                                        onClick={() => deleteClearance()}
                                    />
                                )}
                                <Button
                                    type="submit"
                                    className="btn-outline comex-clearance-table-new-btn"
                                    image="images/figma/icon/nuevo.svg"
                                    label="comex.clearance.new.btn"
                                    disabled={
                                        !submitIsAllowed(allFormik.values) ||
                                        (numberOfEditableItems > 1 && index < numberOfEditableItems - 1)
                                    }
                                />
                            </div>
                        )}
                    </div>
                </Form>
            )}
        </Formik>
    );
};

ClearanceForm.propTypes = {
    isDesktop: bool.isRequired,
    handleAddItem: func.isRequired,
    handleDeleteItem: func.isRequired,
    handleUpdateItem: func.isRequired,
    index: number.isRequired,
    clearenceData: shape({}).isRequired,
    numberOfClearances: number.isRequired,
    updateItemsCompleted: func.isRequired,
    numberOfEditableItems: number.isRequired,
};

export default ClearanceForm;
